// ตั้งค่าระบบ — mirrors GET /system-settings, PATCH /system-settings/:key and
// GET /system-settings/:key/history. Every payload below is the response envelope's `data`.

/** The tab a setting is listed under. */
export type SystemSettingCategory =
  | "report"
  | "pricebook"
  | "customer"
  | "visit"
  | "etax"
  | "qr"
  | "reprint"
  | "purchase_order"
  | "bdc";

/** Decides how a value is shown, which input edits it and how it is checked before saving. */
export type SystemSettingValueType =
  | "decimal"
  | "integer"
  | "integer_list"
  | "text_list"
  | "pricebook_code"
  | "customer_group_code"
  /** One of `constraints.options`, picked from a list. */
  | "choice"
  /** One line of free text, bounded by `min_length` / `max_length`. */
  | "text"
  /** A calendar date, `YYYY-MM-DD`. */
  | "date";

/** `null` is a key with no default that nobody has set yet. */
export type SystemSettingValue = number | string | number[] | string[] | null;

/** Where the effective value came from: a saved row, the key's legacy env variable, or the code default. */
export type SystemSettingSource = "database" | "env" | "default";

/** Bounds a value must stay within. Only the ones that apply to the key's value type are sent. */
export interface SystemSettingConstraints {
  /** Lowest allowed number — the value itself, or every item of an integer list. */
  min?: number;
  /** Highest allowed number — the value itself, or every item of an integer list. */
  max?: number;
  /** Most decimal places allowed; 0 = whole numbers only. */
  scale?: number;
  min_items?: number;
  max_items?: number;
  /** Shortest allowed text — a code, or every item of a text list. */
  min_length?: number;
  /** Longest allowed text — a code, or every item of a text list. */
  max_length?: number;
  /** Every value a choice may take, in the order to offer them. */
  options?: string[];
}

export interface SystemSetting {
  key: string;
  category: SystemSettingCategory;
  value_type: SystemSettingValueType;
  /** The value in force now. */
  value: SystemSettingValue;
  default_value: SystemSettingValue;
  source: SystemSettingSource;
  /** Thai, written by the server. */
  description: string;
  /** Reports are computed when they are produced, so a change only reaches later reports. */
  affects_reports: boolean;
  constraints: SystemSettingConstraints;
  /** null until the key is first edited. */
  updated_by: string | null;
  updated_at: string | null;
}

export interface SystemSettingListResponse {
  rows: SystemSetting[];
  count: number;
}

export interface UpdateSystemSettingRequest {
  value: SystemSettingValue;
}

export interface SystemSettingHistoryRow {
  id: string;
  key: string;
  old_value: SystemSettingValue;
  new_value: SystemSettingValue;
  changed_by: string;
  changed_at: string;
}

/** Newest first. */
export interface SystemSettingHistoryResponse {
  rows: SystemSettingHistoryRow[];
  count: number;
}

/** Every key the server lists. The settings page labels each; a few screens also read one. */
export const SYSTEM_SETTING_KEY = {
  salesReportWasteMultiplier: "sales_report.waste_multiplier",
  inactiveStoreExcludedBranchNumbers: "report.inactive_store.excluded_branch_numbers",
  inactiveStoreExcludedGroups: "report.inactive_store.excluded_groups",
  inactiveStoreExcludedCodePrefixes: "report.inactive_store.excluded_code_prefixes",
  inactiveStoreDefaultDays: "report.inactive_store.default_days",
  inactiveStoreLookbackDays: "report.inactive_store.lookback_days",
  visitFrequencyAlternatePercent: "report.visit_frequency.alternate_percent",
  visitFrequencyTwiceWeeklyPercent: "report.visit_frequency.twice_weekly_percent",
  visitFrequencyMinWorkingDays: "report.visit_frequency.min_frequency_working_days",
  pivotGapBuckets: "report.pivot.gap_buckets",
  pricebookFacePriceCode: "pricebook.face_price_code",
  pricebookSalesDefaultCode: "pricebook.sales_default_code",
  customerGeneralGroupCode: "customer.general_group_code",
  visitGeofenceRadiusM: "visit.geofence_radius_m",
  visitCountWasteMaxQty: "visit.count.waste_max_qty",
  visitCountRemainMaxQty: "visit.count.remain_max_qty",
  visitCountWasteConfirmQty: "visit.count.waste_confirm_qty",
  visitCountRemainConfirmQty: "visit.count.remain_confirm_qty",
  etaxSubmitDeadlineDay: "etax.submit_deadline_day",
  qrExpiryMode: "qr.expiry_mode",
  qrExpiryMinutes: "qr.expiry_minutes",
  reprintGrantDefaultHours: "reprint.grant_default_hours",
  reprintRequestValidHours: "reprint.request_valid_hours",
  reprintRequestCopyQuantity: "reprint.request_copy_quantity",
  purchaseOrderDeliveryWindowDays: "purchase_order.delivery_window_days",
  expressE1StartDate: "express.e1_start_date",
} as const;

/** The values of `qr.expiry_mode`. */
export const QR_EXPIRY_MODE = {
  duration: "DURATION",
  endOfDay: "END_OF_DAY",
} as const;
